import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #isolatedMediaIndex = null;
  #scrollLockEnabled = false;
  #previousScrollY = 0;
  #hadHtmlScrollLock = false;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unlockPageScroll();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    this.#showOnlySelectedMedia(index);

    const { dialog, media } = this.refs;
    const targetImage = media[index];

    const open = () => {
      this.#lockPageScroll();
      dialog.showModal();

      targetImage?.scrollIntoView({ behavior: 'instant' });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectMedia(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog } = this.refs;
    const media = this.#visibleMedia;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();
    if (!media.length) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const realActiveIndex = this.#realMediaIndex(activeIndex);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[realActiveIndex] : mediaGallery?.media?.[realActiveIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');
    await onAnimationEnd(dialog);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#restoreMediaVisibility();
    this.#unlockPageScroll();
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the selection of media.
   * @param {number} index - The index of the media to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectMedia(index, options = { behavior: 'smooth' }) {
    if (!this.#visibleMedia.length) return;
    const visibleIndex = this.#toVisibleIndex(index);
    if (visibleIndex === null) return;

    // Guard if invalid
    if (isNaN(visibleIndex) || visibleIndex < 0 || visibleIndex >= this.#visibleMedia.length) return;

    const media = this.#visibleMedia;
    const targetImage = media[visibleIndex];
    if (!targetImage) return;

    targetImage.scrollIntoView({ behavior: options.behavior });
    this.loadHighResolutionImage(targetImage);
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#realMediaIndex(visibleIndex)));
  }

  #showOnlySelectedMedia(index) {
    this.#restoreMediaVisibility();

    const media = this.refs.media;
    if (!media.length) return;

    const selectedIndex = Math.min(Math.max(index, 0), media.length - 1);
    this.#isolatedMediaIndex = selectedIndex;

    media.forEach((item, i) => {
      item.hidden = i !== selectedIndex;
    });
  }

  #restoreMediaVisibility() {
    if (this.#isolatedMediaIndex === null) return;

    this.refs.media.forEach((item) => {
      item.hidden = false;
    });

    this.#isolatedMediaIndex = null;
  }

  get #visibleMedia() {
    return this.refs.media.filter((item) => !item.hidden);
  }

  #realMediaIndex(visibleIndex) {
    if (this.#isolatedMediaIndex !== null) return this.#isolatedMediaIndex;
    return visibleIndex;
  }

  #toVisibleIndex(index) {
    if (this.#isolatedMediaIndex !== null) {
      return index === this.#isolatedMediaIndex ? 0 : null;
    }

    return index;
  }

  #lockPageScroll() {
    if (this.#scrollLockEnabled) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;
    this.#hadHtmlScrollLock = document.documentElement.hasAttribute('scroll-lock');

    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.documentElement.setAttribute('scroll-lock', '');

    this.#scrollLockEnabled = true;
  }

  #unlockPageScroll() {
    if (!this.#scrollLockEnabled) return;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    if (!this.#hadHtmlScrollLock) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.#scrollLockEnabled = false;
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
